import React, { useState } from "react";
import { Drawer, Card, Badge, Tooltip } from "antd";
import { motion } from "framer-motion";
import redirectUrl from "../../../auth/redirectToApp";
import {
  AppstoreOutlined,
  RocketOutlined,
  CloseOutlined,
  ProjectOutlined,
  ToolOutlined,
  FileSearchOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  ShoppingCartOutlined,
  TrophyOutlined,
  RobotOutlined,
} from "@ant-design/icons";

const ApplicationApps = () => {
  const [openApplicationAppsDrawer, setOpenApplicationAppsDrawer] =
    useState(false);
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);

  const apps = [
    {
      name: "Procure to Pay",
      icon: ShoppingCartOutlined,
      iconColor: "#2563eb",
        bgGradient: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
      redirect: process.env.REACT_APP_PRMT_URL || "",
      permission: false,
      status: "active",
      description: "Manage procurement processes",
    },
    {
      name: "Project Management",
      icon: ProjectOutlined,
      iconColor: "#10b981",
      bgGradient: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
      redirect: process.env.REACT_APP_PMS_URL || "",
      permission: false,
      status: "active",
      description: "Plan and track projects efficiently",
    },
    {
      name: "Work Order Management",
      icon: ToolOutlined,
      iconColor: "#f59e0b",
      bgGradient: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
      permission: false,
      redirect: process.env.REACT_APP_WOM_URL || "",
      status: "active",
      description: "Manage work orders seamlessly",
    },
    {
      name: "Tender Management",
      icon: FileSearchOutlined,
      iconColor: "#06b6d4",
      bgGradient: "linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)",
      permission: false,
      redirect: process.env.REACT_APP_TMS_URL || "",
      status: "active",
      description: "Handle tenders and bids",
    },
    // {
    //   name: "Control Tower",
    //   icon: DashboardOutlined,
    //   iconColor: "#10b981",
    //   bgGradient: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
    //   permission: false,
    //   redirect: process.env.REACT_APP_CONTROL_TOWER_URL || "",
    //   status: "active",
    //   description: "Monitor and control operations",
    // },
    {
      name: "Control X",
      icon: RobotOutlined,
      iconColor: "#8b5cf6",
      bgGradient: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)",
      permission: false,
      redirect: process.env.REACT_APP_CONTROL_X_URL || "",
      status: "active",
      description: "AI-powered assistant for automation",
    },
    {
      name: "Auction",
      icon: TrophyOutlined,
      iconColor: "#f97316",
      bgGradient: "linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)",
      permission: false,
      redirect: process.env.REACT_APP_AUCTION_URL || "",
      status: "coming-soon",
      description: "Participate in auctions and bidding",
    },
    {
      name: "CRM",
      icon: CustomerServiceOutlined,
      iconColor: "#ec4899",
      bgGradient: "linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)",
      permission: false,
      redirect: process.env.REACT_APP_CRM_URL || "",
      status: "coming-soon",
      description: "Customer relationship management",
    },
  ];

  const onSelectApp = async (app: any) => {
    if (app.status === "coming-soon" || !app.redirect) {
      return;
    }

    console.log("onSelectApp", app);
    try {
      if (app.redirect) {
        // Check login method to determine redirect strategy
        const loginMethod = localStorage.getItem("loginMethod");

        if (loginMethod === "azure") {
          // User logged in with Azure - use SSO flow
          redirectUrl(app.redirect);
        } else {
          // User logged in with regular credentials - direct redirect
          window.open(app.redirect, "_blank", "noopener,noreferrer");
        }
      }
    } catch (err) {
      console.error("Error acquiring token:", err);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      } as any,
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring" as const,
        stiffness: 100,
      },
    },
  };

  return (
    <>
      <Tooltip title="Applications" placement="bottom">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={(ev) => {
            ev.preventDefault();
            setOpenApplicationAppsDrawer(!openApplicationAppsDrawer);
          }}
          style={{
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            background: openApplicationAppsDrawer
              ? "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)"
              : "#f1f5f9",
            transition: "all 0.3s ease",
            boxShadow: openApplicationAppsDrawer
              ? "0 4px 12px rgba(37, 99, 235, 0.3)"
              : "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <AppstoreOutlined
            style={{
              fontSize: "20px",
              color: openApplicationAppsDrawer ? "#ffffff" : "#475569",
              transition: "color 0.3s ease",
            }}
          />
        </motion.div>
      </Tooltip>

      <Drawer
        title={null}
        closeIcon={null}
        open={openApplicationAppsDrawer}
        width={380}
        maskClosable={true}
        onClose={() => {
          setOpenApplicationAppsDrawer(false);
        }}
        styles={{
          body: {
            padding: "24px",
            background: "white",
          },
        }}
      >
        {/* Header Section */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "32px",
          }}
        >
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <AppstoreOutlined
              style={{
                fontSize: "28px",
                color: "#2563eb",
              }}
            />
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "700",
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                ProcureX Nexus Suite
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "600",
                }}
              >
                Simplify. Connect. Build.
              </p>
            </div>
          </motion.div>

         {/* <Tooltip title="Close">
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setOpenApplicationAppsDrawer(false)}
              style={{
                border: "none",
                background: "#e2e8f0",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              <CloseOutlined style={{ fontSize: "16px", color: "#475569" }} />
            </motion.button>
          </Tooltip> */}
        </div>

        {/* Apps List */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {apps.map((app: any, index: number) => (
            <motion.div
              key={app.name}
              variants={itemVariants}
              onHoverStart={() => setHoveredApp(app.name)}
              onHoverEnd={() => setHoveredApp(null)}
            >
              <motion.div
                whileHover={{
                  x: 4,
                  transition: { duration: 0.2 },
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onSelectApp(app)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  padding: "14px 16px",
                  borderRadius: "10px",
                  background:
                    hoveredApp === app.name
                      ? "rgba(37, 99, 235, 0.04)"
                      : "transparent",
                  transition: "all 0.2s ease",
                  cursor: app.status === "active" ? "pointer" : "not-allowed",
                  opacity: app.status === "coming-soon" ? 0.6 : 1,
                  position: "relative",
                }}
              >
                {/* Icon on the left */}
                <motion.div
                  animate={{
                    scale: hoveredApp === app.name ? 1.08 : 1,
                  }}
                  transition={{
                    duration: 0.2,
                    ease: "easeInOut",
                  }}
                  style={{
                    width: "52px",
                    height: "52px",
                    minWidth: "52px",
                    borderRadius: "10px",
                    background: app.bgGradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow:
                      hoveredApp === app.name
                        ? `0 4px 12px ${app.iconColor}30`
                        : "0 2px 6px rgba(0,0,0,0.06)",
                    transition: "box-shadow 0.2s ease",
                  }}
                >
                  {app.icon && (
                    <app.icon
                      style={{
                        fontSize: "26px",
                        color: app.iconColor,
                      }}
                    />
                  )}
                </motion.div>

                {/* Content on the right */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "2px",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#1e293b",
                        lineHeight: "1.3",
                      }}
                    >
                      {app.name}
                    </h4>
                    {app.status === "active" && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.1 + 0.3 }}
                      >
                        <RocketOutlined
                          style={{
                            fontSize: "13px",
                            color: "#10b981",
                          }}
                        />
                      </motion.div>
                    )}
                    {app.status === "coming-soon" && (
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          color: "#f59e0b",
                          background: "#fef3c7",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      color: "#64748b",
                      lineHeight: "1.4",
                    }}
                  >
                    {app.description}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Footer Note */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          style={{
            marginTop: "32px",
            padding: "16px",
            background: "rgba(255,255,255,0.6)",
            borderRadius: "12px",
            textAlign: "center",
            backdropFilter: "blur(10px)",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: "#64748b",
            }}
          >
            More apps coming soon to enhance your experience
          </p>
        </motion.div> */}
      </Drawer>
    </>
  );
};

export default ApplicationApps;
